import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Typography,
  Box,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Book as BookIcon,
  Description as ResourceIcon,
  Assignment as LessonPlanIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import type { UserActivity } from '../../types/dashboard';
import { useDashboardStore } from '../../stores/dashboardStore';

interface UserActivityListProps {
  activities: UserActivity[];
  isLoading?: boolean;
  error?: string | null;
}

const getActivityIcon = (action: string, entityType: string) => {
  if (action === 'search') return <SearchIcon />;
  if (action === 'download') return <DownloadIcon />;
  if (entityType === 'book') return <BookIcon />;
  if (entityType === 'resource') return <ResourceIcon />;
  if (entityType === 'lessonplan') return <LessonPlanIcon />;
  return <HistoryIcon />;
};

const getActivityColor = (action: string) => {
  switch (action) {
    case 'borrow': return 'primary';
    case 'return': return 'success';
    case 'view': return 'info';
    case 'download': return 'warning';
    case 'search': return 'secondary';
    default: return 'default';
  }
};

const getActionLabel = (action: string) => {
  switch (action) {
    case 'borrow': return 'Mượn';
    case 'return': return 'Trả';
    case 'view': return 'Xem';
    case 'download': return 'Tải xuống';
    case 'search': return 'Tìm kiếm';
    case 'upload': return 'Tải lên';
    case 'submit': return 'Nộp';
    default: return action;
  }
};

const formatActivityText = (activity: UserActivity) => {
  const { action = 'unknown', entityType = 'item', metadata } = activity;
  
  // Use metadata if available for more descriptive text
  if (metadata) {
    switch (action) {
      case 'borrow':
        return `Đã mượn: ${metadata.bookTitle || metadata.resourceTitle || 'tài liệu'}`;
      case 'return':
        return `Đã trả: ${metadata.bookTitle || metadata.resourceTitle || 'tài liệu'}`;
      case 'view':
        return `Đã xem: ${metadata.bookTitle || metadata.resourceTitle || 'tài liệu'}`;
      case 'download':
        return `Đã tải xuống: ${metadata.resourceTitle || metadata.bookTitle || 'tài liệu'}`;
      case 'search':
        return `Đã tìm kiếm: ${metadata.query || 'từ khóa'}`;
      case 'upload':
        return `Đã tải lên: ${metadata.resourceTitle || 'tài nguyên'}`;
      case 'submit':
        return `Đã nộp: ${metadata.lessonPlanTitle || 'giáo án'}`;
      default:
        return `${getActionLabel(action)}: ${metadata.bookTitle || metadata.resourceTitle || metadata.lessonPlanTitle || 'tài liệu'}`;
    }
  }
  
  // Fallback to generic text
  switch (action) {
    case 'borrow':
      return `Đã mượn ${entityType}`;
    case 'return':
      return `Đã trả ${entityType}`;
    case 'view':
      return `Đã xem ${entityType}`;
    case 'download':
      return `Đã tải xuống ${entityType}`;
    case 'search':
      return 'Đã thực hiện tìm kiếm';
    case 'upload':
      return `Đã tải lên ${entityType}`;
    case 'submit':
      return `Đã nộp ${entityType}`;
    default:
      return `${getActionLabel(action)} ${entityType}`;
  }
};

const formatTimestamp = (timestamp: string) => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
  
  if (diffInHours < 1) return 'Vừa xong';
  if (diffInHours < 24) return `${diffInHours} giờ trước`;
  if (diffInHours < 168) return `${Math.floor(diffInHours / 24)} ngày trước`;
  return date.toLocaleDateString('vi-VN');
};

export const UserActivityList: React.FC<UserActivityListProps> = ({
  activities,
  isLoading = false,
  error = null
}) => {
  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader title="Hoạt động gần đây" />
      <CardContent>
        {activities.length === 0 ? (
          <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
            Không có hoạt động gần đây
          </Typography>
        ) : (
          <List>
            {activities.map((activity) => (
              <ListItem key={activity.id} divider>
                <ListItemIcon>
                  {getActivityIcon(activity.action, activity.entityType)}
                </ListItemIcon>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body2" component="div">
                    {formatActivityText(activity)}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                    <Chip
                      label={getActionLabel(activity.action || 'unknown')}
                      size="small"
                      color={getActivityColor(activity.action) as any}
                      variant="outlined"
                    />
                    <Typography variant="caption" color="text.secondary" component="span">
                      {formatTimestamp(activity.timestamp || new Date().toISOString())}
                    </Typography>
                  </Box>
                </Box>
              </ListItem>
            ))}
          </List>
        )}
      </CardContent>
    </Card>
  );
};
